import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Clock, User, Phone, MessageSquare, CheckCircle2, AlertCircle, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const API_URL = process.env.REACT_APP_API_URL;
const DAY_NAMES_BY_LANG = {
  tr: ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'],
  en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  ru: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
};

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function next14Days() {
  const out = [];
  const today = new Date();
  for (let i = 0; i < 14; i += 1) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + i);
    out.push(d);
  }
  return out;
}

function formatPhone(s) {
  // Just keep digits + leading 0/5
  return (s || '').replace(/[^\d+\s-]/g, '');
}

const StepHeader = ({ step, title }) => (
  <div className="flex items-center gap-3 mb-3" data-testid={`step-${step}-header`}>
    <div className="w-7 h-7 rounded-full bg-[#2563EB] text-white flex items-center justify-center text-sm font-semibold">{step}</div>
    <h3 className="text-base font-semibold text-[#0F172A]">{title}</h3>
  </div>
);

const PublicBooking = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || 'tr';
  const DAY_NAMES = DAY_NAMES_BY_LANG[lang] || DAY_NAMES_BY_LANG.tr;
  const locale = lang === 'tr' ? 'tr-TR' : lang === 'ru' ? 'ru-RU' : 'en-US';
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state
  const [service, setService] = useState(null); // {name, duration_min, default_price}
  const [staff, setStaff] = useState(null); // {id, name} | null
  const [date, setDate] = useState(null); // YYYY-MM-DD
  const [slot, setSlot] = useState(null); // HH:MM
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Availability data
  const [slots, setSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);

  // Success state
  const [done, setDone] = useState(null); // { status, message }

  // ----- fetch business meta on mount -----
  useEffect(() => {
    document.title = t('publicBooking.header') + ' • Paydaai';
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/public/business/${encodeURIComponent(slug)}`);
        if (cancel) return;
        setBiz(data);
        document.title = `${data.name} • ${t('publicBooking.header')}`;
        // Inject OG tags client-side (so social previews from browsers/JS-aware crawlers work)
        const setMeta = (selector, attrs) => {
          let el = document.querySelector(selector);
          if (!el) {
            el = document.createElement('meta');
            Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
            document.head.appendChild(el);
          } else {
            if (attrs.content !== undefined) el.setAttribute('content', attrs.content);
          }
        };
        const desc = `${data.name} ${t('publicBooking.header')}`;
        setMeta('meta[property="og:title"]', { property: 'og:title', content: `${data.name} • ${t('publicBooking.header')}` });
        setMeta('meta[property="og:description"]', { property: 'og:description', content: desc });
        setMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
        setMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
        setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: `${data.name} • ${t('publicBooking.header')}` });
        setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: desc });
        const img = data.logo_url || `${window.location.origin}/logo-paydaai.png`;
        setMeta('meta[property="og:image"]', { property: 'og:image', content: img });
        setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: img });
      } catch (err) {
        if (cancel) return;
        const detail = err.response?.data?.detail || t('publicBooking.errorTitle');
        setError(detail);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [slug, t]);

  // Effective service duration: explicit value from custom_services, or fall back
  // to the business's slot_interval (default 30) when the catalog only gave a name.
  const effectiveDuration = useMemo(() => {
    if (!service) return null;
    if (service.duration_min && service.duration_min > 0) return service.duration_min;
    return biz?.config?.slot_interval || 30;
  }, [service, biz]);

  // ----- fetch available slots when date+service+(optional staff) change -----
  useEffect(() => {
    if (!biz || !service || !date || !effectiveDuration) {
      setSlots(null);
      return;
    }
    let cancel = false;
    (async () => {
      setSlotsLoading(true);
      try {
        const params = { slug, date, duration: effectiveDuration };
        if (staff?.id) params.staff_id = staff.id;
        const { data } = await axios.get(`${API_URL}/api/public/availability`, { params });
        if (!cancel) setSlots(data.slots || []);
      } catch (err) {
        if (!cancel) setSlots([]);
        if (!cancel) toast.error(err.response?.data?.detail || t('common.error'));
      } finally {
        if (!cancel) setSlotsLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [biz, service, date, staff, slug, effectiveDuration]);

  // Reset selected slot if it's no longer in the returned list
  useEffect(() => {
    if (slot && slots && !slots.includes(slot)) setSlot(null);
  }, [slots]);  // eslint-disable-line react-hooks/exhaustive-deps

  const canSubmit = useMemo(() => {
    if (!service || !date || !slot) return false;
    if (biz?.config?.staff_required && !staff?.id) return false;
    if (!name.trim() || name.trim().length < 2) return false;
    if (!phone || phone.replace(/\D/g, '').length < 7) return false;
    return true;
  }, [service, date, slot, staff, name, phone, biz]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const body = {
        slug,
        service: service.name,
        date,
        start_time: slot,
        duration_min: effectiveDuration,
        name: name.trim(),
        phone: phone.trim(),
        notes: notes.trim() || null,
        staff_id: staff?.id || null,
      };
      const { data } = await axios.post(`${API_URL}/api/public/appointments`, body);
      setDone({ status: data.status, message: data.message });
    } catch (err) {
      const code = err.response?.status;
      const detail = err.response?.data?.detail || 'Bir hata oluştu';
      toast.error(detail);
      if (code === 409) {
        // re-fetch slots so the just-taken time disappears
        setSlot(null);
        if (biz && service && date && effectiveDuration) {
          try {
            const params = { slug, date, duration: effectiveDuration };
            if (staff?.id) params.staff_id = staff.id;
            const { data: av } = await axios.get(`${API_URL}/api/public/availability`, { params });
            setSlots(av.slots || []);
          } catch { /* ignore */ }
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ----- render branches -----
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-sm text-[#595959]">{t('publicBooking.loadingTitle')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-[#E5E5E5] p-8 text-center shadow-sm" data-testid="pb-error">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-[#0F172A] mb-2">{t('publicBooking.errorTitle')}</h1>
          <p className="text-sm text-[#595959]">{error}</p>
          <div className="mt-6 flex justify-center"><LanguageSwitcher /></div>
        </div>
      </div>
    );
  }

  if (done) {
    const ok = done.status === 'scheduled';
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-[#E5E5E5] p-8 text-center shadow-sm" data-testid="pb-done">
          <CheckCircle2 className={`w-14 h-14 mx-auto mb-3 ${ok ? 'text-[#16A34A]' : 'text-[#2563EB]'}`} />
          <h1 className="text-xl font-bold text-[#0F172A] mb-2">
            {ok ? t('publicBooking.doneScheduled') : t('publicBooking.doneRequested')}
          </h1>
          <p className="text-sm text-[#595959] mb-6">{done.message}</p>
          <div className="text-left text-sm bg-[#F9FAFB] rounded-lg p-4 space-y-1 border border-[#E5E5E5]">
            <p><span className="text-[#595959]">{t('publicBooking.doneBusiness')}:</span> <strong>{biz.name}</strong></p>
            <p><span className="text-[#595959]">{t('publicBooking.doneService')}:</span> <strong>{service.name}</strong></p>
            <p><span className="text-[#595959]">{t('publicBooking.doneDate')}:</span> <strong>{date}</strong> <strong>{slot}</strong></p>
            {staff?.name && <p><span className="text-[#595959]">{t('publicBooking.doneStaff')}:</span> <strong>{staff.name}</strong></p>}
            <p><span className="text-[#595959]">{t('publicBooking.donePhone')}:</span> <strong>{phone}</strong></p>
          </div>
          <button
            type="button"
            className="mt-6 inline-flex items-center gap-1 text-sm text-[#2563EB] hover:underline"
            onClick={() => { setDone(null); setSlot(null); setService(null); setStaff(null); setDate(null); setName(''); setPhone(''); setNotes(''); }}
            data-testid="pb-new-booking-btn"
          >
            <ChevronLeft className="w-4 h-4" /> {t('publicBooking.newBooking')}
          </button>
        </div>
      </div>
    );
  }

  const days = next14Days();
  const closed = biz?.config?.closed_dates || [];
  const workingDays = biz?.settings?.working_days || [0, 1, 2, 3, 4, 5];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-3xl mx-auto px-4 py-5 flex items-center gap-3" data-testid="pb-header">
          {biz?.logo_url ? (
            <img src={biz.logo_url} alt={biz.name} className="w-12 h-12 rounded-xl object-cover" />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-[#2563EB] text-white flex items-center justify-center font-bold text-lg">
              {biz?.name?.slice(0, 1)?.toUpperCase() || 'P'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold text-[#0F172A] truncate" data-testid="pb-biz-name">{biz?.name}</h1>
            <p className="text-xs text-[#595959]">{t('publicBooking.header')}</p>
          </div>
          <LanguageSwitcher variant="compact" />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Step 1: Service */}
        <section className="bg-white rounded-2xl border border-[#E5E5E5] p-5 shadow-sm" data-testid="pb-section-service">
          <StepHeader step={1} title={t('publicBooking.step1Service')} />
          {(biz?.services || []).length === 0 ? (
            <p className="text-sm text-[#595959]">{t('publicBooking.noServices')}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {biz.services.map((s) => {
                const active = service?.name === s.name;
                const hasPrice = s.default_price !== null && s.default_price !== undefined && s.default_price > 0;
                const hasDuration = s.duration_min !== null && s.duration_min !== undefined && s.duration_min > 0;
                const subtitle = [
                  hasDuration ? `${s.duration_min} ${t('publicBooking.minutesShort')}` : null,
                  hasPrice ? `${s.default_price.toLocaleString(locale)} ${t('publicBooking.currencyTl')}` : null,
                ].filter(Boolean).join(' · ');
                return (
                  <button
                    key={s.name}
                    type="button"
                    onClick={() => { setService(s); setSlot(null); }}
                    className={`text-left rounded-xl border px-3 py-3 transition ${
                      active ? 'border-[#2563EB] bg-[#EFF6FF]' : 'border-[#E5E5E5] hover:border-[#2563EB]/40 bg-white'
                    }`}
                    data-testid={`pb-service-${s.name}`}
                  >
                    <p className="text-sm font-semibold text-[#0F172A]">{s.name}</p>
                    {subtitle && <p className="text-xs text-[#595959] mt-0.5">{subtitle}</p>}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Step 2: Staff */}
        {service && (biz?.staff || []).length > 0 && (
          <section className="bg-white rounded-2xl border border-[#E5E5E5] p-5 shadow-sm" data-testid="pb-section-staff">
            <StepHeader step={2} title={biz?.config?.staff_required ? t('publicBooking.step2Staff') : t('publicBooking.step2StaffOptional')} />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {!biz?.config?.staff_required && (
                <button
                  type="button"
                  onClick={() => { setStaff(null); setSlot(null); }}
                  className={`rounded-xl border px-3 py-2 text-sm font-medium transition ${
                    !staff ? 'border-[#2563EB] bg-[#EFF6FF] text-[#2563EB]' : 'border-[#E5E5E5] hover:border-[#2563EB]/40'
                  }`}
                  data-testid="pb-staff-any"
                >
                  {t('publicBooking.anyStaff')}
                </button>
              )}
              {(biz.staff || []).map((st) => {
                const active = staff?.id === st.id;
                return (
                  <button
                    key={st.id}
                    type="button"
                    onClick={() => { setStaff(st); setSlot(null); }}
                    className={`rounded-xl border px-3 py-2 text-sm font-medium transition ${
                      active ? 'border-[#2563EB] bg-[#EFF6FF] text-[#2563EB]' : 'border-[#E5E5E5] hover:border-[#2563EB]/40'
                    }`}
                    data-testid={`pb-staff-${st.id}`}
                  >
                    {st.name}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Step 3: Date — shown immediately if no staff needed, or after staff pick */}
        {service && ((biz?.staff || []).length === 0 || !biz?.config?.staff_required || staff !== null) && (
          <section className="bg-white rounded-2xl border border-[#E5E5E5] p-5 shadow-sm" data-testid="pb-section-date">
            <StepHeader step={(biz?.staff || []).length > 0 ? 3 : 2} title={t('publicBooking.step3Date')} />
            <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
              {days.map((d) => {
                const iso = isoDate(d);
                const wd = (d.getDay() + 6) % 7; // JS Sun=0 → Mon=0
                const isClosed = closed.includes(iso) || !workingDays.includes(wd);
                const active = date === iso;
                return (
                  <button
                    key={iso}
                    type="button"
                    disabled={isClosed}
                    onClick={() => { setDate(iso); setSlot(null); }}
                    className={`flex flex-col items-center rounded-xl border py-2 text-xs transition ${
                      isClosed
                        ? 'border-[#F3F4F6] bg-[#F9FAFB] text-[#CBD5E1] cursor-not-allowed line-through'
                        : active
                          ? 'border-[#2563EB] bg-[#EFF6FF] text-[#2563EB] font-semibold'
                          : 'border-[#E5E5E5] hover:border-[#2563EB]/40 text-[#0F172A]'
                    }`}
                    data-testid={`pb-date-${iso}`}
                  >
                    <span>{DAY_NAMES[wd]}</span>
                    <span className="text-base">{d.getDate()}</span>
                    <span className="text-[10px] text-[#595959]">{d.toLocaleString(locale, { month: 'short' })}</span>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Step 4: Time */}
        {service && date && (
          <section className="bg-white rounded-2xl border border-[#E5E5E5] p-5 shadow-sm" data-testid="pb-section-time">
            <StepHeader step={(biz?.staff || []).length > 0 ? 4 : 3} title={t('publicBooking.step4Time')} />
            {slotsLoading ? (
              <p className="text-sm text-[#595959]">{t('publicBooking.loadingSlots')}</p>
            ) : !slots || slots.length === 0 ? (
              <div className="text-sm text-[#595959] bg-[#F9FAFB] border border-[#E5E5E5] rounded-lg p-3" data-testid="pb-no-slots">
                {t('publicBooking.noSlots')}
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {slots.map((t) => {
                  const active = slot === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setSlot(t)}
                      className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                        active
                          ? 'border-[#2563EB] bg-[#2563EB] text-white'
                          : 'border-[#E5E5E5] hover:border-[#2563EB]/40 text-[#0F172A]'
                      }`}
                      data-testid={`pb-slot-${t}`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {/* Step 5: Contact info + submit */}
        {service && date && slot && (
          <section className="bg-white rounded-2xl border border-[#E5E5E5] p-5 shadow-sm space-y-4" data-testid="pb-section-contact">
            <StepHeader step={(biz?.staff || []).length > 0 ? 5 : 4} title={t('publicBooking.step5Contact')} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm flex items-center gap-1"><User className="w-3.5 h-3.5" />{t('publicBooking.fullName')}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('publicBooking.fullNamePlaceholder')} data-testid="pb-name-input" />
              </div>
              <div>
                <Label className="text-sm flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{t('publicBooking.phone')}</Label>
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  placeholder={t('publicBooking.phonePlaceholder')}
                  data-testid="pb-phone-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" />{t('publicBooking.notes')}</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder={t('publicBooking.notesPlaceholder')} data-testid="pb-notes-input" />
            </div>

            <div className="bg-[#F9FAFB] rounded-lg p-3 border border-[#E5E5E5] text-sm space-y-0.5">
              <div className="flex items-center gap-2 text-[#0F172A]"><Calendar className="w-4 h-4 text-[#2563EB]" /><strong>{date}</strong> · <Clock className="w-4 h-4 text-[#2563EB]" /> <strong>{slot}</strong></div>
              <div className="text-[#595959]">{service.name}{effectiveDuration ? ` · ${effectiveDuration} ${t('publicBooking.minutesShort')}` : ''}{staff?.name ? ` · ${staff.name}` : ''}</div>
            </div>

            <Button
              type="submit"
              disabled={!canSubmit || submitting}
              className="w-full h-12 bg-[#2563EB] hover:bg-[#1E4ED8] text-white text-base"
              data-testid="pb-submit-btn"
            >
              {submitting ? t('publicBooking.submitting') : biz?.config?.auto_confirm ? t('publicBooking.submitConfirm') : t('publicBooking.submitRequest')}
            </Button>
            {!biz?.config?.auto_confirm && (
              <p className="text-xs text-[#595959] text-center">{t('publicBooking.approvalNote')}</p>
            )}
          </section>
        )}

        <p className="text-center text-xs text-[#9CA3AF] py-4" data-testid="pb-footer">
          {t('publicBooking.footerPoweredBy')} <a href="https://paydaai.com" className="text-[#2563EB] hover:underline" target="_blank" rel="noopener noreferrer">Paydaai</a>
        </p>
      </form>
    </div>
  );
};

export default PublicBooking;
